import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { fireEvent } from '@/lib/analytics';

export default function CheckoutError() {
  const [searchParams] = useSearchParams();
  const errorMessage =
    searchParams.get('message') || 'An unexpected error occurred during checkout.';

  useEffect(() => {
    // Track checkout error
    fireEvent('checkout_error', {
      reason: errorMessage,
    });
  }, [errorMessage]);

  return (
    <>
      <Helmet>
        <title>Checkout Error | Attain Domains</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Error Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Checkout Error</h1>
            <p className="text-muted-foreground">
              I encountered an issue processing your checkout.
            </p>
          </div>

          {/* Error Details */}
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>

          {/* Help Card */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">What can you do?</h2>
              <ul className="space-y-2 text-muted-foreground mb-4">
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>Try checking out again from your cart</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>Verify your payment information with a trusted payment provider</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>Contact me for assistance</span>
                </li>
              </ul>

              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-3">
                  <strong>Need help?</strong> Our team is here to assist you with any checkout
                  issues.
                </p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/contact">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="flex-1" size="lg">
              <Link to="/cart">Try Again</Link>
            </Button>
            <Button asChild variant="outline" className="flex-1" size="lg">
              <Link to="/domains">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Domains
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
